/**
 * Marketplace Entitlements — webhook events from AWS / GCP marketplace.
 *
 * Layout:
 *   PageHeader → HeroPanel (total / processed / pending / error)
 *              → Provider × status matrix (heatmap)
 *              → Toolbar (status + provider chip filters)
 *              → DataTable
 *              → EntitlementDrawer (full payload + approve unknown product)
 *
 * Note: the plan called for a "customers × plugins matrix" but the
 * data shape doesn't expose customers per-row (license_id is optional)
 * and plugins aren't first-class — the closest actionable matrix is
 * provider × status, where pending/error cells flag where the
 * reconciler is stuck.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  Store, RefreshCw, Trash2, ExternalLink, KeyRound, CheckCircle2,
  AlertOctagon, Grid3x3,
} from 'lucide-react';
import {
  AdminApiClient,  type MarketplaceEntitlementRow,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

type StatusFilter = 'all' | 'pending' | 'processed' | 'error';
type ProviderFilter = 'all' | 'aws_marketplace' | 'gcp_marketplace';
type Provider = 'aws_marketplace' | 'gcp_marketplace';

const PROVIDER_LABEL: Record<Provider, string> = {
  aws_marketplace: 'AWS',
  gcp_marketplace: 'GCP',
};
const PROVIDER_TONE: Record<Provider, string> = {
  aws_marketplace: '#fb923c',
  gcp_marketplace: '#22d3ee',
};
const STATUSES = ['processed', 'pending', 'error'] as const;
type Status = typeof STATUSES[number];

function bandForStatus(status: string): Band {
  switch (status) {
    case 'processed': return 'healthy';
    case 'pending':   return 'warning';
    case 'error':     return 'critical';
    default:          return 'neutral';
  }
}

export function MarketplaceEntitlementsPage(): JSX.Element {
  const { state } = useSession();
  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  const [rows, setRows] = useState<MarketplaceEntitlementRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [provider, setProvider] = useState<ProviderFilter>('all');
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const perms = usePermissions();
  const canMutate = perms.canWrite;
  const canDelete = perms.canOwner;

  useEffect(() => {
    setRefreshing(true);
    api.listMarketplaceEntitlements({
      status: status === 'all' ? undefined : status,
      provider: provider === 'all' ? undefined : provider,
      limit: 500,
    })
      .then((r) => { setRows(r.marketplace_entitlements); setErr(null); })
      .catch((e) => setErr(failureFrom(e, 'marketplace entitlements')))
      .finally(() => setRefreshing(false));
  }, [api, status, provider, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('Delete this entitlement record? Audit log keeps a snapshot.')) return;
    try {
      await api.deleteMarketplaceEntitlement(id);
      setOpenId(null);
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const counts = useMemo(() => {
    let total = 0, processed = 0, pending = 0, errors = 0;
    for (const r of rows ?? []) {
      total += 1;
      if (r.status === 'processed') processed += 1;
      else if (r.status === 'pending') pending += 1;
      else if (r.status === 'error') errors += 1;
    }
    return { total, processed, pending, errors };
  }, [rows]);

  const focused = useMemo(
    () => (rows ?? []).find((r) => r.id === openId) ?? null,
    [rows, openId],
  );

  const columns: Column<MarketplaceEntitlementRow>[] = [
    {
      key: 'received',
      header: 'Received',
      sortable: true,
      sortValue: (r) => Date.parse(r.received_at),
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {fmtRelative(r.received_at)}
        </span>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      cell: (r) => <ProviderChip provider={r.provider} />,
    },
    {
      key: 'external',
      header: 'External id',
      cell: (r) => (
        <PrimaryWithSub
          primary={<code style={{ fontSize: '0.75rem' }}>{r.external_id}</code>}
          sub={productCodeFromPayload(r.payload_json) ?? undefined}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <StatusChip band={bandForStatus(r.status)}>{r.status}</StatusChip>,
    },
    {
      key: 'action',
      header: 'Action taken',
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {r.action_taken ?? '—'}
        </span>
      ),
    },
    {
      key: 'license',
      header: 'License',
      cell: (r) => r.license_id ? (
        <code style={{ fontSize: '0.75rem' }}>{r.license_id.slice(0, 8)}…</code>
      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
  ];

  const statusChips: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: 'all',       label: 'All',       count: counts.total },
    { key: 'processed', label: 'Processed', count: counts.processed },
    { key: 'pending',   label: 'Pending',   count: counts.pending },
    { key: 'error',     label: 'Error',     count: counts.errors },
  ];
  const providerChips: Array<{ key: ProviderFilter; label: string; count: number }> = [
    { key: 'all',             label: 'Any', count: rows?.length ?? 0 },
    { key: 'aws_marketplace', label: 'AWS', count: (rows ?? []).filter((r) => r.provider === 'aws_marketplace').length },
    { key: 'gcp_marketplace', label: 'GCP', count: (rows ?? []).filter((r) => r.provider === 'gcp_marketplace').length },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Marketplace entitlements"
        subtitle="Webhook events from AWS + GCP Marketplace. The reconciler patches local state from these."
        icon={Store}
        accent="#fb923c"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <AccessNotice reason={canMutate ? null : `${perms.writeReason ?? ''} Approving and deleting entitlements is not available to you.`} />

      {rows && (<>
      <HeroPanel
        icon={Store}
        accent="#fb923c"
        title="Reconciler load"
        subtitle="Inbound webhook events vs what's been applied to local licensing state."
        kpis={[
          <HeroKpi key="t" label="Total" value={counts.total} hint="visible window" />,
          <HeroKpi key="p" label="Processed" value={counts.processed}
                   band={counts.processed > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="w" label="Pending" value={counts.pending}
                   band={counts.pending > 0 ? 'warning' : 'neutral'}
                   hint="awaiting reconciler" />,
          <HeroKpi key="e" label="Error" value={counts.errors}
                   band={counts.errors > 0 ? 'critical' : 'healthy'}
                   hint="needs operator attention" />,
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <ProviderStatusMatrix rows={rows} onPick={(p, s) => {
          setProvider(p);
          setStatus(s);
        }} />
      </div>
      </>)}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <ChipRow chips={statusChips} value={status} onChange={setStatus} />
          <ChipRow chips={providerChips} value={provider} onChange={setProvider} />
        </div>

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenId(r.id)}
          loading={!rows && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle="No entitlements match"
          emptyDescription="Adjust filters or refresh — entitlements appear as marketplace webhooks land."
        />
      </div>

      <EntitlementDrawer
        api={api}
        row={focused}
        canDelete={canDelete}
        canMutate={canMutate}
        onClose={() => setOpenId(null)}
        onSaved={onRefresh}
        onDelete={focused ? () => void onDelete(focused.id) : undefined}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Provider × Status matrix
 * ────────────────────────────────────────────────────────────────── */

function ProviderStatusMatrix({
  rows, onPick,
}: {
  rows: MarketplaceEntitlementRow[];
  onPick: (provider: ProviderFilter, status: StatusFilter) => void;
}): JSX.Element {
  const cells = useMemo(() => {
    const matrix: Record<Provider, Record<Status, number>> = {
      aws_marketplace: { processed: 0, pending: 0, error: 0 },
      gcp_marketplace: { processed: 0, pending: 0, error: 0 },
    };
    for (const r of rows) {
      const s = r.status as Status;
      if (matrix[r.provider] && (s === 'processed' || s === 'pending' || s === 'error')) {
        matrix[r.provider][s] += 1;
      }
    }
    return matrix;
  }, [rows]);

  const max = Math.max(
    1,
    ...(['aws_marketplace', 'gcp_marketplace'] as Provider[]).flatMap((p) =>
      STATUSES.map((s) => cells[p][s]),
    ),
  );

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Grid3x3 width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Provider × status · click a cell to filter
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `120px repeat(${STATUSES.length}, minmax(80px, 1fr))`,
          gap: 6,
          alignItems: 'center',
        }}
      >
        <span />
        {STATUSES.map((s) => (
          <span
            key={s}
            style={{
              fontSize: '0.6875rem', color: 'var(--text-muted)',
              textAlign: 'center', textTransform: 'capitalize',
            }}
          >
            {s}
          </span>
        ))}

        {(['aws_marketplace', 'gcp_marketplace'] as Provider[]).map((p) => (
          <MatrixRow
            key={p}
            provider={p}
            counts={cells[p]}
            max={max}
            onPick={(s) => onPick(p, s)}
          />
        ))}
      </div>
    </section>
  );
}

function MatrixRow({
  provider, counts, max, onPick,
}: {
  provider: Provider;
  counts: Record<Status, number>;
  max: number;
  onPick: (s: StatusFilter) => void;
}): JSX.Element {
  const tone = PROVIDER_TONE[provider];
  return (
    <>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: '0.8125rem', fontWeight: 500,
          color: tone,
          paddingRight: 8,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 2, background: tone }} />
        {PROVIDER_LABEL[provider]}
      </span>
      {STATUSES.map((s) => {
        const v = counts[s];
        const ratio = v === 0 ? 0 : 0.2 + 0.8 * (v / max);
        const baseHue = s === 'processed' ? 152 : s === 'pending' ? 36 : 0;
        const bg = v === 0
          ? 'rgba(148,163,184,0.06)'
          : `hsla(${baseHue} 80% 50% / ${ratio})`;
        return (
          <button
            key={s}
            onClick={() => onPick(s)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 38,
              borderRadius: 8,
              background: bg,
              border: v === 0 ? '1px solid rgba(148,163,184,0.10)' : '1px solid rgba(148,163,184,0.20)',
              color: ratio > 0.5 ? '#0b0f19' : 'var(--text-primary)',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            {v}
          </button>
        );
      })}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Provider chip
 * ────────────────────────────────────────────────────────────────── */

function ProviderChip({ provider }: { provider: Provider }): JSX.Element {
  const tone = PROVIDER_TONE[provider];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.6875rem', fontWeight: 500,
        background: `${tone}1f`,
        color: tone,
        border: `1px solid ${tone}40`,
      }}
    >
      {PROVIDER_LABEL[provider]}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Drawer with full payload + approve flow
 * ────────────────────────────────────────────────────────────────── */

function EntitlementDrawer({
  api, row, canMutate, canDelete, onClose, onSaved, onDelete,
}: {
  api: AdminApiClient;
  row: MarketplaceEntitlementRow | null;
  canMutate: boolean;
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDelete?: () => void;
}): JSX.Element {
  const [planTier, setPlanTier] = useState<'starter' | 'business' | 'enterprise'>('business');
  const [maxActivations, setMaxActivations] = useState(5);
  const [features, setFeatures] = useState('core,ai-doctor');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form whenever drawer opens onto a different row.
  useEffect(() => {
    if (row) {
      setPlanTier('business');
      setMaxActivations(5);
      setFeatures('core,ai-doctor');
      setErr(null);
    }
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isUnknownProduct = row && (
    row.error?.includes('unknown_product') === true
    || row.action_taken === 'unknown_product'
  );

  const submit = async (): Promise<void> => {
    if (!row) return;
    setBusy(true); setErr(null);
    try {
      await api.approveUnknownMarketplaceProduct(row.id, {
        plan_tier: planTier,
        max_activations: maxActivations,
        features: features.split(',').map((s) => s.trim()).filter(Boolean),
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="lg"
      title={
        row ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Store width={14} height={14} />
            <code style={{ fontSize: '0.875rem' }}>{row.external_id}</code>
          </span>
        ) : ''
      }
      subtitle={row ? `${PROVIDER_LABEL[row.provider]} · ${row.status}` : undefined}
      footer={
        row && (
          <>
            {canDelete && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 width={14} height={14} />
                Delete
              </Button>
            )}
            {canMutate && isUnknownProduct && (
              <Button
                variant="default"
                size="sm"
                onClick={() => void submit()}
                disabled={busy}
              >
                <CheckCircle2 width={14} height={14} />
                {busy ? 'Approving…' : 'Approve product'}
              </Button>
            )}
          </>
        )
      }
    >
      {row && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}

          <Section title="Event">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Provider"><ProviderChip provider={row.provider} /></KV>
              <KV label="Status">
                <StatusChip band={bandForStatus(row.status)}>{row.status}</StatusChip>
              </KV>
              <KV label="Received">{fmtAbsolute(row.received_at)}</KV>
              <KV label="Processed">
                {row.processed_at ? fmtAbsolute(row.processed_at) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
              </KV>
              <KV label="Action taken">
                {row.action_taken ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
              </KV>
              <KV label="License">
                {row.license_id ? (
                  <a
                    href={`/licenses?focus=${encodeURIComponent(row.license_id)}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      color: 'var(--accent-text)', textDecoration: 'none',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <KeyRound width={11} height={11} />
                    <code style={{ fontSize: '0.75rem' }}>{row.license_id.slice(0, 12)}…</code>
                    <ExternalLink width={11} height={11} />
                  </a>
                ) : <span style={{ color: 'var(--text-muted)' }}>none</span>}
              </KV>
            </div>
          </Section>

          {row.error && (
            <Section title="Error">
              <p
                style={{
                  margin: 0,
                  padding: 12,
                  borderRadius: 8,
                  background: 'var(--danger-soft)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: 'var(--danger)',
                  fontSize: '0.8125rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <AlertOctagon width={14} height={14} />
                {row.error}
              </p>
            </Section>
          )}

          {canMutate && isUnknownProduct && (
            <Section
              title="Approve unknown product"
              hint="Creates a product mapping; the next reconciler pass provisions the license."
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Field label="Plan tier">
                  <select
                    value={planTier}
                    onChange={(e) => setPlanTier(e.target.value as 'starter' | 'business' | 'enterprise')}
                    style={inputStyle}
                  >
                    <option value="starter">starter</option>
                    <option value="business">business</option>
                    <option value="enterprise">enterprise</option>
                  </select>
                </Field>
                <Field label="Max activations">
                  <input
                    type="number" min={1} max={10000}
                    value={maxActivations}
                    onChange={(e) => setMaxActivations(Math.max(1, Number(e.target.value) || 1))}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Features" hint="Comma-separated">
                  <input
                    value={features}
                    onChange={(e) => setFeatures(e.target.value)}
                    style={inputStyle}
                  />
                </Field>
              </div>
            </Section>
          )}

          <Section title="Webhook payload">
            {payloadRedactionNote(row) ? (
              <div
                data-testid="payload-redaction-note"
                style={{ marginBottom: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}
              >
                {payloadRedactionNote(row)}
              </div>
            ) : null}
            <pre
              style={{
                margin: 0,
                padding: 12,
                borderRadius: 8,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                fontSize: '0.6875rem',
                fontFamily: 'ui-monospace, monospace',
                color: 'var(--text-muted)',
                whiteSpace: 'pre-wrap',
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              {payloadText(row.payload_json)}
            </pre>
          </Section>

          <Section title="Identifiers">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <div>id: <code>{row.id}</code></div>
              <div>external_id: <code>{row.external_id}</code></div>
            </div>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function Section({
  title, hint, children,
}: { title: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      {children}
      {hint && (
        <p style={{ margin: '6px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {hint}
        </p>
      )}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ fontSize: '0.8125rem' }}>{children}</div>
    </div>
  );
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
  fontFamily: 'inherit',
} as const;

function payloadText(payload: Record<string, unknown> | null | undefined): string {
  if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0) {
    return '[redacted: no payload fields available for your role]';
  }
  return JSON.stringify(payload, null, 2);
}

/** Product code from the (possibly redacted, empty or missing) payload view. */
export function productCodeFromPayload(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as { productCode?: unknown; ProductCode?: unknown; product?: unknown; entitlement?: unknown };
  const ent = p.entitlement && typeof p.entitlement === 'object'
    ? (p.entitlement as { product?: unknown }).product : undefined;
  const code = p.productCode ?? p.ProductCode ?? p.product ?? ent;
  if (typeof code === 'string' && code) return code;
  return null;
}

/** Plain-language note about what the server withheld from this role's payload view. */
export function payloadRedactionNote(row: Pick<MarketplaceEntitlementRow, 'payload_view' | 'payload_omitted_fields' | 'payload_redacted'>): string | null {
  if (!row.payload_redacted && row.payload_omitted_fields === undefined) return null;
  const n = row.payload_omitted_fields;
  const count = typeof n === 'number' && n > 0 ? ` ${n} field${n === 1 ? '' : 's'} withheld.` : '';
  switch (row.payload_view) {
    case 'owner': return `Redacted view: secrets, tokens and free text are never shown.${count}`;
    case 'admin': return `Redacted view: customer identifiers and prices are shown to Owner accounts only; secrets, tokens and free text to nobody.${count}`;
    case 'none': return `The payload is not available to your role.${count}`;
    default: return `Redacted view: your role sees event and product fields only.${count}`;
  }
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtAbsolute(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + 'Z';
}
